import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  Stack,
} from '@mui/material';

export default function BillingSuccessLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [countdown, setCountdown] = useState(3);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');

  const sessionId = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      return params.get('session_id');
    } catch (_) {
      return null;
    }
  }, [location.search]);

  useEffect(() => {
    // Show success view immediately for at least 3 seconds
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Set post-billing flag to allow onboarding pages to be visited without redirect to /chat
    try { localStorage.setItem('postBillingOnboarding', '1'); } catch (_) {}
    // Best-effort auto-clear after 10 minutes
    const clearTimer = setTimeout(() => {
      try { localStorage.removeItem('postBillingOnboarding'); } catch (_) {}
    }, 10 * 60 * 1000);
    return () => clearTimeout(clearTimer);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const verifyAndPoll = async () => {
      setVerifying(true);
      setError('');
      try {
        // 1) Optionally verify checkout session (idempotent). Works with or without auth header
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const headers = { 'Content-Type': 'application/json' };
          if (session?.access_token) {
            headers['Authorization'] = `Bearer ${session.access_token}`;
            headers['apikey'] = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
          }
          if (sessionId) {
            const resp = await fetch(`/functions/v1/verify-checkout-session`, {
              method: 'POST',
              headers,
              body: JSON.stringify({ sessionId }),
            });
            // Non-fatal if fails; webhooks still handle it
            if (!resp.ok) {
              // Consume body for debugging but ignore error
              try { await resp.text(); } catch (_) {}
            }
          }
        } catch (e) {
          // Ignore verification errors - webhook will reconcile
          console.warn('[BillingSuccessLanding] verify-checkout-session error (non-fatal):', e);
        }

        // 2) Poll for user auth + profile + active subscription readiness (max ~20s)
        const start = Date.now();
        let isReady = false;
        while (!cancelled && Date.now() - start < 20000) {
          const { data: { session } } = await supabase.auth.getSession();
          const user = session?.user || null;
          if (!user) {
            await new Promise((r) => setTimeout(r, 500));
            continue;
          }
          const userId = user.id;
          const { data: profile, error: profErr } = await supabase
            .from('user_profiles')
            .select('company_id')
            .eq('user_id', userId)
            .maybeSingle();
          if (profErr && profErr.code !== 'PGRST116') {
            console.warn('[BillingSuccessLanding] profile lookup error (non-fatal):', profErr);
          }
          const companyId = profile?.company_id || null;
          if (!companyId) {
            await new Promise((r) => setTimeout(r, 500));
            continue;
          }
          const { data: sub, error: subErr } = await supabase
            .from('company_subscriptions')
            .select('status')
            .eq('company_id', companyId)
            .eq('status', 'active')
            .maybeSingle();
          if (subErr) {
            console.warn('[BillingSuccessLanding] subscription lookup error (non-fatal):', subErr);
          }
          if (sub) {
            isReady = true;
            break;
          }
          await new Promise((r) => setTimeout(r, 500));
        }
        if (!cancelled) setVerifying(false);
        if (!cancelled && countdown === 0) {
          proceed();
        }
      } catch (e) {
        if (!cancelled) {
          setVerifying(false);
          setError(e.message || 'Unexpected error after payment.');
        }
      }
    };

    verifyAndPoll();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (!verifying && countdown === 0) {
      proceed();
    }
  }, [verifying, countdown]);

  const proceed = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        navigate('/onboarding/profile', { replace: true });
      } else {
        navigate('/login?redirect_to=/onboarding/profile', { replace: true });
      }
    } catch (_) {
      navigate('/login?redirect_to=/onboarding/profile', { replace: true });
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'success.light', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h4" component="div" sx={{ color: 'success.dark' }}>✓</Typography>
            </Box>
          </Box>
          <Typography variant="h4" gutterBottom>
            Payment successful
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Thank you! Your subscription is being finalized.
          </Typography>

          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Redirecting to onboarding in {countdown}s...
            </Typography>
          </Stack>

          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>
          )}

          <Box sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={proceed}>Continue now</Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}


